import { useEffect, useRef } from "react";

const TARGET_SAMPLE_RATE = 16000;

const toBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    const chunkSize = 0x8000;
    for (let index = 0; index < bytes.length; index += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
    }
    return window.btoa(binary);
};

const resampleTo16k = (samples, inputRate) => {
    if (inputRate === TARGET_SAMPLE_RATE) return samples;
    const ratio = inputRate / TARGET_SAMPLE_RATE;
    const output = new Float32Array(Math.round(samples.length / ratio));
    for (let index = 0; index < output.length; index += 1) {
        const start = Math.floor(index * ratio);
        const end = Math.min(Math.floor((index + 1) * ratio), samples.length);
        let sum = 0;
        for (let sourceIndex = start; sourceIndex < end; sourceIndex += 1) sum += samples[sourceIndex];
        output[index] = sum / Math.max(1, end - start);
    }
    return output;
};

const floatToPcm16 = (samples) => {
    const pcm = new ArrayBuffer(samples.length * 2);
    const view = new DataView(pcm);
    samples.forEach((sample, index) => {
        const clipped = Math.max(-1, Math.min(1, sample));
        view.setInt16(index * 2, clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff, true);
    });
    return pcm;
};

// The browser never sees the Sarvam key. It streams PCM to our Django proxy.
export const useSarvamLiveTranscript = ({
    audioStream,
    enabled,
    questionText,
    onTranscript,
    onLanguageMismatch,
    onError,
}) => {
    const onTranscriptRef = useRef(onTranscript);
    const onLanguageMismatchRef = useRef(onLanguageMismatch);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onTranscriptRef.current = onTranscript;
        onLanguageMismatchRef.current = onLanguageMismatch;
        onErrorRef.current = onError;
    }, [onTranscript, onLanguageMismatch, onError]);
    useEffect(() => {
        // Start a fresh upstream Sarvam session only when candidate capture is
        // allowed. This prevents a partial decoded from the system announcement
        // from surviving in Sarvam's VAD/audio buffer into the candidate answer.
        if (!enabled || !audioStream?.getAudioTracks().length) return undefined;

        // const backendUrl =
        //   process.env.REACT_APP_VIVAX_WS_URL ||
        //   process.env.REACT_APP_WS_URL ||
        //   "ws://localhost:8000";
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const backendUrl = `${protocol}//${window.location.host}`;
        const encodedQuestion = encodeURIComponent((questionText || "").slice(0, 1000));
        console.log("backendUrl ", backendUrl)
        const socket = new WebSocket(
            `${backendUrl.replace(/\/$/, "")}/ws/live-transcript/?question_text=${encodedQuestion}`,
        );
        socket.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                if (message.type === "transcript") {
                    onTranscriptRef.current?.(message.text, Boolean(message.is_final));
                }
                if (message.type === "language_mismatch") {
                    onLanguageMismatchRef.current?.(message.expected_language_name);
                }
                if (message.type === "error") onErrorRef.current?.(message.message);
            } catch {
                onErrorRef.current?.("Invalid live-transcription response");
            }
        };
        socket.onerror = () => onErrorRef.current?.("Live-transcription connection failed");

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const context = new AudioContextClass();
        const source = context.createMediaStreamSource(audioStream);
        const processor = context.createScriptProcessor(4096, 1, 1);
        const silentGain = context.createGain();
        silentGain.gain.value = 0;
        processor.onaudioprocess = (event) => {
            if (socket.readyState !== WebSocket.OPEN) return;
            const samples = resampleTo16k(event.inputBuffer.getChannelData(0), context.sampleRate);
            socket.send(JSON.stringify({ type: "audio", data: toBase64(floatToPcm16(samples)) }));
        };
        source.connect(processor);
        processor.connect(silentGain);
        silentGain.connect(context.destination);

        context.resume().catch(() => {
            onErrorRef.current?.("Microphone audio processing could not start");
        });

        return () => {
            if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "flush" }));
            socket.close();
            processor.disconnect();
            source.disconnect();
            silentGain.disconnect();
            context.close();
        };
    }, [audioStream, enabled, questionText]);
};
